import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import TuneIcon from "@mui/icons-material/Tune";
import { isMobile } from "../../globalStyles/mobile";
import { forecastDataService } from "../../utils/services/forecast-data.service";
import { formatDateTime } from "../../utils/formatDate";
import {
  useMetricsUi,
  PAGE_FONT,
  MONO_FONT,
} from "../../components/metrics/metricsUi";
import ForecastParamsPanel from "./ForecastParamsPanel";
import ForecastResults from "./ForecastResults";
import ForecastRunningSteps from "./ForecastRunningSteps";
import { latestBatchRuns } from "./latestBatchRuns";

const OPTION_PAGE = 20;

const optionLabel = (by, o) => {
  if (!o) return "";
  if (by === "Product") {
    const n = o.sku_count || 0;
    return `${o.label}  ·  ${n} SKU${n === 1 ? "" : "s"}`;
  }
  return o.latest_at ? `${o.label}  ·  ${formatDateTime(o.latest_at)}` : o.label;
};

const skuOf = (run) => run?.sku || run?.input?.sku || "";

const PlanningForecast = () => {
  const ui = useMetricsUi();
  const { colors } = ui;
  const clientId = localStorage.getItem("client_id");

  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // History browser — step 1: Product | SKU
  const [historyBy, setHistoryBy] = useState("Product");
  // step 2: the paginated list of products / SKUs seen in stored runs
  const [options, setOptions] = useState([]);
  const [optionsTotal, setOptionsTotal] = useState(0);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [historyKey, setHistoryKey] = useState("");

  // Actual forecast-data docs behind the current selection.
  const [historyRuns, setHistoryRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);

  const reqId = useRef(0);
  // In-flight request for each endpoint — aborted whenever a newer call
  // supersedes it, so a rapid sequence of dropdown/tab changes doesn't leave
  // several redundant forecast-data requests running concurrently.
  const optionsAbortRef = useRef(null);
  const runsAbortRef = useRef(null);

  // Abort anything still in flight when the page itself unmounts.
  useEffect(() => {
    return () => {
      optionsAbortRef.current?.abort();
      runsAbortRef.current?.abort();
    };
  }, []);

  // Fetch a page of history options for `by`; append or replace.
  const fetchOptions = useCallback(
    async (by, { append = false } = {}) => {
      optionsAbortRef.current?.abort();
      const controller = new AbortController();
      optionsAbortRef.current = controller;
      setOptionsLoading(true);
      try {
        const res = await forecastDataService.groups(clientId, {
          by: by.toLowerCase(),
          limit: OPTION_PAGE,
          skip: append ? options.length : 0,
          signal: controller.signal,
        });
        const rows = Array.isArray(res?.data) ? res.data : [];
        setOptions((prev) => (append ? [...prev, ...rows] : rows));
        setOptionsTotal(res?.total || 0);
        return res;
      } catch (e) {
        console.error("Failed to load forecast history options", e);
        if (!append) {
          setOptions([]);
          setOptionsTotal(0);
        }
        return null;
      } finally {
        if (optionsAbortRef.current === controller) setOptionsLoading(false);
      }
    },
    [clientId, options.length],
  );

  // Initial load — prefer Product history, fall back to SKU, else open the form.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      let res = await fetchOptions("Product", { append: false });
      if (cancelled) return;
      if (!(res?.total > 0)) {
        setHistoryBy("SKU");
        res = await fetchOptions("SKU", { append: false });
        if (cancelled) return;
      }
      if (!(res?.total > 0)) setEditing(true);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  // Keep the step-2 selection valid: default to the first option.
  useEffect(() => {
    setHistoryKey((prev) =>
      prev && options.some((o) => o.value === prev)
        ? prev
        : options[0]?.value || "",
    );
  }, [options]);

  // Load the forecast-data docs for the current selection.
  useEffect(() => {
    runsAbortRef.current?.abort();
    if (!historyKey) {
      setHistoryRuns([]);
      return undefined;
    }
    const myReq = ++reqId.current;
    const controller = new AbortController();
    runsAbortRef.current = controller;
    setRunsLoading(true);
    (async () => {
      try {
        const res =
          historyBy === "Product"
            ? await forecastDataService.list(clientId, {
                product: historyKey,
                forecastBy: "product",
                limit: 300,
                signal: controller.signal,
              })
            : await forecastDataService.list(clientId, {
                skuExact: historyKey,
                forecastBy: "sku",
                limit: 1,
                signal: controller.signal,
              });
        if (myReq !== reqId.current) return;
        // The server already scopes to runs generated AT this level — an
        // individual SKU re-run under a product (or vice versa) is a
        // different, ad-hoc thing (different horizon/params) and shouldn't
        // mix into this history.
        const list = Array.isArray(res?.data) ? res.data : [];
        if (historyBy === "Product") {
          // One tab per SKU: its record from the product's latest forecast
          // batch, or its own newest record if it wasn't in that batch —
          // the same records the Product Artifact is built from.
          setHistoryRuns(latestBatchRuns(list, (r) => skuOf(r) || r._id));
        } else {
          // SKU mode — just the latest run for this SKU.
          setHistoryRuns(list.slice(0, 1));
        }
      } catch (e) {
        if (myReq === reqId.current) {
          console.error("Failed to load forecast runs", e);
          setHistoryRuns([]);
        }
      } finally {
        if (myReq === reqId.current) setRunsLoading(false);
      }
    })();
    return () => controller.abort();
  }, [clientId, historyBy, historyKey]);

  const selected = useMemo(() => historyRuns[0] || null, [historyRuns]);

  // Which run's tab is actually on screen inside <ForecastResults> — kept in
  // sync via its onActiveRunChange so the Forecast Parameters panel reflects
  // whichever SKU/product the user picked, not always the first run. Cleared
  // whenever the run set itself changes, so a stale selection from the
  // previous SKU/product never flashes before ForecastResults reports back
  // its own (reset) tab.
  const [activeRun, setActiveRun] = useState(null);
  // Whether "All SKUs" (the whole product), not one specific SKU, is the
  // active selection — passed down so the Forecast Parameters panel can
  // hide the SKU field instead of showing one arbitrary SKU's code.
  const [isAllSkusView, setIsAllSkusView] = useState(false);
  const handleActiveRunChange = useCallback((run, meta) => {
    setActiveRun(run);
    setIsAllSkusView(Boolean(meta?.isAllSkus));
  }, []);
  // Only `activeRun` is cleared here — <ForecastResults> always reports the
  // freshly-resolved run (and isAllSkus) right after this fires, since its
  // own effect for the new `historyRuns` runs AFTER this one (child effects
  // fire before parent effects). Clearing `isAllSkusView` here too would
  // clobber that correct report back to `false` every time, which is
  // invisible for `activeRun` (ForecastParamsPanel falls back to `selected`)
  // but not for `isAllSkusView`, which has no such fallback.
  useEffect(() => {
    setActiveRun(null);
  }, [historyRuns]);

  const changeHistoryBy = (value) => {
    setHistoryBy(value);
    setHistoryKey("");
    setHistoryRuns([]);
    setOptions([]);
    setOptionsTotal(0);
    fetchOptions(value, { append: false });
  };

  const onForecasted = async (ids) => {
    setEditing(false);
    const list = Array.isArray(ids) ? ids : ids ? [ids] : [];
    if (!list.length) return;
    let doc = null;
    try {
      const res = await forecastDataService.get(list[0]);
      doc = res?.data || null;
    } catch (e) {
      console.error("Failed to load the new forecast run", e);
    }
    const by =
      String(doc?.input?.forecast_by || "").toLowerCase() === "product"
        ? "Product"
        : "SKU";
    const key =
      by === "Product"
        ? String(doc?.input?.product?._id || doc?.input?.product || "")
        : String(doc?.sku || "");
    setHistoryBy(by);
    setOptions([]);
    setOptionsTotal(0);
    await fetchOptions(by, { append: false });
    if (key) setHistoryKey(key);
  };

  const canLoadMore = options.length < optionsTotal;
  const hasHistory = options.length > 0;

  return (
    <Box
      sx={{
        backgroundColor: ui.darkMode ? colors.bgPrimary : "#F5F6F8",
        minHeight: "100%",
        fontFamily: PAGE_FONT,
      }}
    >
      <Box
        sx={{
          width: "100%",
          padding: isMobile() ? "14px 12px 40px" : "22px 28px 56px",
        }}
      >
        {/* ---------- Topbar (sticky — stays put while results scroll) ----------
            The scroll container is <main> (padding: 16px). We bleed this bar out
            over that padding + this page's own padding on every side, then add it
            back as inner padding, so the bar spans edge-to-edge and its solid
            background fully hides content scrolling underneath. `top: -16px`
            pins it flush with the top of the scroll area. */}
        <Box
          sx={{
            position: "sticky",
            top: "-16px",
            zIndex: 10,
            backgroundColor: ui.darkMode ? colors.bgPrimary : "#F5F6F8",
            borderBottom: `1px solid ${colors.border}`,
            mx: isMobile() ? "-28px" : "-44px",
            px: isMobile() ? "28px" : "44px",
            mt: isMobile() ? "-30px" : "-38px",
            pt: isMobile() ? "30px" : "38px",
            pb: 1.5,
            mb: 2,
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: "9px",
                background: `linear-gradient(155deg, ${ui.accent}, #1B3FA8)`,
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: 13,
                fontFamily: MONO_FONT,
                flexShrink: 0,
              }}
            >
              FC
            </Box>
            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 700,
                color: colors.textPrimary,
                lineHeight: 1,
              }}
            >
              Forecast
            </Typography>

            {hasHistory && !editing && (
              <>
                {/* Step 1 — browse history by Product or by SKU */}
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <InputLabel id="pf-mode-label">History By</InputLabel>
                  <Select
                    labelId="pf-mode-label"
                    label="History By"
                    value={historyBy}
                    onChange={(e) => changeHistoryBy(e.target.value)}
                    sx={{
                      backgroundColor: colors.bgPaper,
                      fontSize: 13,
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: colors.border,
                      },
                    }}
                  >
                    <MenuItem value="Product" sx={{ fontSize: 13 }}>
                      Product
                    </MenuItem>
                    <MenuItem value="SKU" sx={{ fontSize: 13 }}>
                      SKU
                    </MenuItem>
                  </Select>
                </FormControl>

                {/* Step 2 — the products / SKUs seen in recent forecast runs */}
                <FormControl
                  size="small"
                  sx={{ minWidth: 260, maxWidth: "100%" }}
                >
                  <InputLabel id="pf-key-label">
                    {historyBy === "Product" ? "Product" : "SKU"}
                  </InputLabel>
                  <Select
                    labelId="pf-key-label"
                    label={historyBy === "Product" ? "Product" : "SKU"}
                    value={
                      options.some((o) => o.value === historyKey)
                        ? historyKey
                        : ""
                    }
                    onChange={(e) => setHistoryKey(e.target.value)}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
                    sx={{
                      backgroundColor: colors.bgPaper,
                      fontSize: 13,
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: colors.border,
                      },
                    }}
                  >
                    {options.map((o) => (
                      <MenuItem
                        key={o.value}
                        value={o.value}
                        sx={{ fontSize: 13 }}
                      >
                        {optionLabel(historyBy, o)}
                      </MenuItem>
                    ))}
                    {canLoadMore && (
                      <ListSubheader
                        disableSticky
                        sx={{
                          p: 0,
                          bgcolor: "transparent",
                          lineHeight: "normal",
                        }}
                      >
                        <Button
                          fullWidth
                          size="small"
                          // keep the menu open — this row isn't a selectable option
                          onClick={() =>
                            fetchOptions(historyBy, { append: true })
                          }
                          disabled={optionsLoading}
                          sx={{
                            fontSize: 12,
                            textTransform: "none",
                            py: 0.75,
                            borderRadius: 0,
                          }}
                        >
                          {optionsLoading
                            ? "Loading…"
                            : `Load more (${optionsTotal - options.length})`}
                        </Button>
                      </ListSubheader>
                    )}
                  </Select>
                </FormControl>
              </>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1.25,
            }}
          >
            <Button
              variant="outlined"
              color="primary"
              startIcon={<TuneIcon />}
              onClick={() => window.open("/forecast", "_blank", "noopener")}
            >
              Custom
            </Button>
            {!editing && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AutorenewIcon />}
                onClick={() => setEditing(true)}
              >
                New
              </Button>
            )}
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
            <CircularProgress />
          </Box>
        ) : !editing && !hasHistory ? (
          <Box
            sx={{
              ...ui.card,
              textAlign: "center",
              py: 7,
              px: 3,
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 16,
                color: colors.textPrimary,
                mb: 0.5,
              }}
            >
              No forecasts yet
            </Typography>
            <Typography
              sx={{ color: colors.textSecondary, fontSize: 13, mb: 2 }}
            >
              Run a forecast for one or more SKUs to see its parameters and
              projected demand here.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AutorenewIcon />}
              onClick={() => setEditing(true)}
            >
              Run Forecast
            </Button>
          </Box>
        ) : !editing && !selected ? (
          runsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ ...ui.card, textAlign: "center", py: 7, px: 3 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: 16,
                  color: colors.textPrimary,
                  mb: 0.5,
                }}
              >
                No forecast runs found
              </Typography>
              <Typography
                sx={{ color: colors.textSecondary, fontSize: 13, mb: 2 }}
              >
                Pick a different {historyBy === "Product" ? "product" : "SKU"}{" "}
                from the history list, or run a new forecast.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AutorenewIcon />}
                onClick={() => setEditing(true)}
              >
                Run Forecast
              </Button>
            </Box>
          )
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Parameters (view / inline-edit) */}
            <ForecastParamsPanel
              run={activeRun || selected}
              clientId={clientId}
              editing={editing}
              onCancel={() => setEditing(false)}
              onForecasted={onForecasted}
              onSubmittingChange={setSubmitting}
              isAllSkusView={isAllSkusView}
            />

            {/* Result skeleton while a run is in flight */}
            {editing && submitting && <ForecastRunningSteps />}

            {/* Result — one or more forecast runs, shown together in tabs */}
            {!editing &&
              (runsLoading ? (
                <Box
                  sx={{ display: "flex", justifyContent: "center", py: 6 }}
                >
                  <CircularProgress size={26} />
                </Box>
              ) : (
                historyRuns.length > 0 && (
                  <ForecastResults
                    runs={historyRuns}
                    onActiveRunChange={handleActiveRunChange}
                  />
                )
              ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PlanningForecast;
